import { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import {
	Alert,
	Button,
	Card,
	Empty,
	Input,
	Segmented,
	Select,
	Space,
	Spin,
	Typography,
} from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';

import Header from '../../../../components/Header';
import MaterialHistoryDrawer from '../../../../components/MaterialMovements/MaterialHistoryDrawer';
import { useGetBuildingsQuery } from '../../../../redux/api/location';
import {
	PendingCutBar,
	PendingCutsMethod,
	useGetPendingCutsReportQuery,
} from '../../../../redux/api/pending-cuts';
import { useLocationLabel } from '../../../../utils/locationLabel';
import {
	METHODS,
	METHOD_LABELS,
	PendingCutsFilters,
	filtersFromSearchParams,
	filtersToSearchParams,
	toReportParams,
} from './filters';
import PendingCutsTable from './PendingCutsTable';
import PrintablePendingCutsReport from './PrintablePendingCutsReport';
import './print.css';

const { Text, Title } = Typography;

const PRINT_MODE_CLASS = 'pending-cuts-print-mode';
/** Injected rather than in print.css: `@page` cannot be scoped to one page. */
const PRINT_PAGE_RULE = '@page { size: letter landscape; margin: 0.4in; }';

/**
 * Pre-cut audit: every reservation still waiting on the saw, bar by bar, so
 * an auditor can confirm each bar's location, length and job before it is cut.
 */
const PendingCuts = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const filters = useMemo(
		() => filtersFromSearchParams(searchParams),
		[searchParams]
	);
	const [barDraft, setBarDraft] = useState(filters.bar);
	const [historyBar, setHistoryBar] = useState<PendingCutBar | null>(null);
	const [printMounted, setPrintMounted] = useState(false);
	const [printPending, setPrintPending] = useState(false);

	useEffect(() => setBarDraft(filters.bar), [filters.bar]);

	const apply = (patch: Partial<PendingCutsFilters>) =>
		setSearchParams(filtersToSearchParams({ ...filters, ...patch }), {
			replace: true,
		});

	const { data, currentData, isFetching, isError, error, refetch } =
		useGetPendingCutsReportQuery(toReportParams(filters));
	// `data` keeps the previous filters on screen while the next load runs; only
	// `currentData` matches the filters, so it alone may be printed.
	const ready = Boolean(currentData) && !isError;
	const showBuilding = filters.buildings.length !== 1;

	const formatLocation = useLocationLabel();
	const { data: buildings } = useGetBuildingsQuery();
	const buildingOptions = useMemo(
		() => (buildings ?? []).map((b) => ({ value: b.id, label: b.name })),
		[buildings]
	);

	useEffect(() => {
		if (!ready) {
			setPrintMounted(false);
			setPrintPending(false);
			return;
		}
		const style = document.createElement('style');
		style.media = 'print';
		style.textContent = PRINT_PAGE_RULE;
		document.head.appendChild(style);
		document.body.classList.add(PRINT_MODE_CLASS);
		const onBeforePrint = () => {
			flushSync(() => setPrintMounted(true));
		};
		const onAfterPrint = () => {
			setPrintMounted(false);
			setPrintPending(false);
		};
		window.addEventListener('beforeprint', onBeforePrint);
		window.addEventListener('afterprint', onAfterPrint);
		return () => {
			style.remove();
			document.body.classList.remove(PRINT_MODE_CLASS);
			window.removeEventListener('beforeprint', onBeforePrint);
			window.removeEventListener('afterprint', onAfterPrint);
		};
	}, [ready]);

	useEffect(() => {
		if (!printMounted || !printPending) return;
		const id = window.setTimeout(() => {
			window.print();
			setPrintPending(false);
		}, 0);
		return () => window.clearTimeout(id);
	}, [printMounted, printPending]);

	const handlePrint = useCallback(() => {
		flushSync(() => setPrintMounted(true));
		setPrintPending(true);
	}, []);

	const errorDetail =
		(error as { data?: { detail?: string } } | undefined)?.data?.detail ??
		'The report could not be loaded.';
	const hasFilters = filters.buildings.length > 0 || Boolean(filters.bar);

	return (
		<>
			<Header
				title="Pending Cuts"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Reports' },
					{ title: 'Pending Cuts' },
				]}
			/>

			<Space direction="vertical" size="middle" style={{ width: '100%' }}>
				<Card size="small" className="shadow-sm">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<Space wrap>
							<Segmented
								data-testid="pending-cuts-method"
								value={filters.method}
								options={METHODS.map((value) => ({
									value,
									label: METHOD_LABELS[value],
								}))}
								onChange={(value) =>
									apply({ method: value as PendingCutsMethod })
								}
							/>
							<Select
								mode="multiple"
								allowClear
								aria-label="Buildings"
								placeholder="All buildings"
								style={{ minWidth: 180 }}
								maxTagCount="responsive"
								value={filters.buildings}
								options={buildingOptions}
								onChange={(next: string[]) => apply({ buildings: next })}
							/>
							<Input.Search
								allowClear
								aria-label="Bar ID"
								placeholder="Bar ID (e.g. AAB)"
								style={{ width: 200 }}
								value={barDraft}
								onChange={(event) => setBarDraft(event.target.value)}
								onSearch={(value) => apply({ bar: value.trim().toUpperCase() })}
							/>
							{hasFilters && (
								<Button onClick={() => apply({ buildings: [], bar: '' })}>
									Clear filters
								</Button>
							)}
						</Space>
						<Space wrap>
							<Button
								icon={<PrinterOutlined />}
								disabled={!ready || isFetching}
								onClick={handlePrint}
							>
								Print
							</Button>
							<Button
								icon={<ReloadOutlined />}
								loading={isFetching}
								onClick={() => refetch()}
							>
								Refresh
							</Button>
						</Space>
					</div>
				</Card>

				{isError && (
					<Alert
						type="error"
						showIcon
						message={errorDetail}
						action={<Button onClick={() => refetch()}>Retry</Button>}
					/>
				)}

				{!data && isFetching && (
					<div className="flex justify-center py-12">
						<Spin />
					</div>
				)}

				{data && !isError && (
					<Spin spinning={isFetching}>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<Title level={5} style={{ margin: 0 }}>
								{data.summary.reservations} reservations · {data.summary.bars}{' '}
								bars · {data.summary.pending_pieces} pieces to cut ·{' '}
								{data.summary.flagged_bars} bars flagged
							</Title>

							{data.truncated && (
								<Alert
									type="warning"
									showIcon
									message={`Showing the first ${data.row_cap.toLocaleString()} reservations.`}
									description="Narrow the report with a building or bar filter to see the rest."
								/>
							)}

							{data.groups.length ? (
								<PendingCutsTable
									groups={data.groups}
									formatLocation={formatLocation}
									onOpenHistory={setHistoryBar}
									showBuilding={showBuilding}
								/>
							) : (
								<Card className="shadow-sm">
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description={
											hasFilters
												? 'No pending cuts match these filters.'
												: 'Nothing is waiting to be cut.'
										}
									/>
								</Card>
							)}

							<Text type="secondary" className="text-xs">
								Lists every reservation not yet marked Cut, including jobs whose
								CAM programming is not complete (flagged “Not saw ready”; the
								saw queue hides those). Fastlane includes every reservation that
								is not Swiss. “Left after cuts” is the bar length minus all of
								its pending cuts and kerf, plus one trim for the bar. A building
								filter omits bars with no location (they have no building), so
								the “No location” flag only appears on All buildings.
							</Text>
						</Space>
					</Spin>
				)}
			</Space>

			{ready && currentData && printMounted && (
				<PrintablePendingCutsReport
					data={currentData}
					filters={filters}
					buildingOptions={buildingOptions}
					formatLocation={formatLocation}
				/>
			)}

			<MaterialHistoryDrawer
				materialLengthId={historyBar?.id ?? null}
				friendlyId={historyBar?.friendly_id ?? undefined}
				open={Boolean(historyBar)}
				onClose={() => setHistoryBar(null)}
			/>
		</>
	);
};

export default PendingCuts;
